import type { ComponentProps } from '@enonic/react-components'
import type { BreadcrumbItem } from '../primitives'
import type { CtaButton } from '../../utils/ctaUtils'
import * as React from 'react'
import { ButtonLink, Container } from '../primitives'

export interface InfoPageData extends Record<string, unknown> {
  title: string
  lead?: string
  ctaPrimary?: CtaButton
  ctaSecondary?: CtaButton
}

export const InfoPage = (props: ComponentProps) => {
  const data = props.data as InfoPageData
  const commonData = props.common as Record<string, unknown> | undefined
  const breadcrumbs = commonData?.breadcrumbs as BreadcrumbItem[] | undefined
  const { title, lead, ctaPrimary, ctaSecondary } = data

  return (
    <section data-hero className="bg-blue-900 mb-12 md:mb-20">
      <Container className="flex flex-col gap-6 pt-10 pb-16 md:gap-8 md:pt-16 md:pb-24">
        {breadcrumbs && breadcrumbs.length > 1 && (
          <nav aria-label="Brødsmuler" className="col-span-full">
            {/* Mobile: single back link to parent */}
            <a
              href={breadcrumbs[breadcrumbs.length - 2].url}
              className="inline-flex items-center gap-1 text-base leading-snug tracking-wide text-slate-300 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300 md:hidden"
            >
              <span aria-hidden="true">←</span>
              {breadcrumbs[breadcrumbs.length - 2].title}
            </a>
            {/* Desktop: full breadcrumb */}
            <ol className="hidden items-center gap-2 text-lg leading-snug tracking-wide md:flex">
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1
                return (
                  <li key={index} className="flex items-center gap-2">
                    {index > 0 && (
                      <span className="text-slate-300" aria-hidden="true">
                        /
                      </span>
                    )}
                    {isLast ? (
                      <span className="text-white" aria-current="page">
                        {item.title}
                      </span>
                    ) : (
                      <a
                        href={item.url}
                        className="text-slate-300 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300"
                      >
                        {item.title}
                      </a>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
        )}

        <div className="col-span-full flex flex-col gap-5 md:col-span-8">
          <h1 className="font-serif text-4xl leading-tightest text-white md:text-7xl">
            {title}
          </h1>
          {lead && (
            <p className="max-w-prose text-xl leading-snug text-slate-300 md:text-2xl">
              {lead}
            </p>
          )}
          {(ctaPrimary || ctaSecondary) && (
            <div className="flex gap-2 md:gap-3">
              {ctaPrimary && (
                <ButtonLink
                  href={ctaPrimary.url}
                  label={ctaPrimary.label}
                  variant="button-white"
                />
              )}
              {ctaSecondary && (
                <ButtonLink
                  href={ctaSecondary.url}
                  label={ctaSecondary.label}
                  variant="button"
                />
              )}
            </div>
          )}
        </div>
      </Container>
    </section>
  )
}
